import styled from '@emotion/styled';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import {
  COLOR_BORDER,
  COLOR_BUTTON_BACKGROUND,
  COLOR_BUTTON_TEXT,
  COLOR_DISABLE_BACKGROUND,
  COLOR_EFFECT,
  COLOR_TEXT_DISABLE,
  COLOR_TEXT_MAIN,
} from '../../theme/colors';
import StyledInputField from '../component/primitive/StyledInputField';
import { PageContainer, PageHeader, PageTitle, PaperButton } from '../component/primitive/Page';
import {
  PaperContent,
  PaperItem,
  PaperText,
  PaperTitle,
  PaperTitleText,
  StyledPaper,
} from '../component/primitive/Paper';
import {
  DrawerButton,
  DrawerCancelButton,
  DrawerContainer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  StyledDrawer,
} from '../component/primitive/Drawer';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { lastAccessedOrganizationVar } from '../../cache';
import {
  ConversionGoal,
  CopyConversionGoalDocument,
  DeleteConversionGoalDocument,
  GetConversionGoalDocument,
  GetConversionGoalQueryVariables,
  GetConversionGoalsDocument,
  GetOrganizationDocument,
  GetOrganizationQuery,
  GetOrganizationQueryVariables,
  PostConversionGoalDocument,
} from '../../generated/graphql';
import getGoalType from '../../utility/getGoalType';
import StyledTextarea from '../component/primitive/StyledTextarea';
import Loading from '../component/compound/Loading';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import GoalTypeIcon from '../component/compound/GoalSetting/GoalTypeIcon';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import StyledBackdrop from '../component/compound/GoalSetting/StyledBackdrop';
import ExperimentGoalsImage from '../../assets/experiment_goals.png';
import globalState from '../../operations/commands';
import Button from '../component/primitive/Button';
import LoadingButton from '../component/primitive/LoadingButton';
// import { context as DependencyContext } from '../context/dependency';

type Props = {
  className?: string;
};

type Order = 'asc' | 'desc';
type OrderType = 'id' | 'title' | 'trigger_type' | 'projects_count';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy === 'trigger_type') {
    if ((b as any).trigger.type < (a as any).trigger.type) {
      return -1;
    }
    if ((b as any).trigger.type > (a as any).trigger.type) {
      return 1;
    }
    return 0;
  }

  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}
function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const GoalSettingPage: FC<Props> = ({ className }) => {
  // const dependency = useContext(DependencyContext);
  // const notifyUnimportant = dependency.messageDispatcher.notifyUnimportant;

  const navigate = useNavigate();
  const [openPostDrawer, setOpenPostDrawer] = useState(false);
  const [goal, setGoal] = useState('');
  const [description, setDescription] = useState('');
  const [radioValue, setRadioValue] = useState('page_visit');
  const [transactionRadioValue, setTransactionRadioValue] = useState('amount');
  const [goalFormatError, setGoalFormatError] = useState(false);
  const [descriptionFormatError, setDescriptionFormatError] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState('');
  const { id: organization_id } = useReactiveVar(lastAccessedOrganizationVar)!;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDelete, setOpenDelete] = useState<boolean>(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    setSelectedGoalId(id);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const { data: k2organization, loading: organizationLoading } = useQuery<
    GetOrganizationQuery,
    GetOrganizationQueryVariables
  >(GetOrganizationDocument, {
    variables: { id: organization_id },
    context: { clientName: 'planbcd' },
    skip: !organization_id,
  });

  const { data, refetch, loading } = useQuery<{ conversionGoals: ConversionGoal[] }, GetConversionGoalQueryVariables>(
    GetConversionGoalsDocument,
    {
      variables: { id: organization_id },
      context: { clientName: 'planbcd' },
      skip:
        !organization_id ||
        k2organization === undefined ||
        !k2organization.getOrganization.permissions.create_event_goals,
      onError: (e) => {
        console.log('onError', e);
      },
    },
  );

  const { data: selectedGoal, loading: selectedGoalLoading } = useQuery<{ conversionGoal: ConversionGoal }>(
    GetConversionGoalDocument,
    {
      variables: { id: selectedGoalId },
      skip: !selectedGoalId,
      context: { clientName: 'planbcd' },
    },
  );

  /* eslint no-empty-pattern: 0 */
  const [runCreateGoal, { loading: createGoalLoading }] = useMutation(PostConversionGoalDocument, {
    context: { clientName: 'planbcd' },
    variables: {
      id: organization_id,
      input: {
        organization_id: organization_id,
        goal: {
          title: goal,
          description: description,
          trigger: {
            type: radioValue,
          },
          transaction_type: radioValue === 'transaction' ? transactionRadioValue : undefined,
          trigger_type: radioValue,
        },
      },
    },
    onCompleted: () => {
      refetch();
      closePostDrawer();
    },
    onError: (e) => {
      console.log('onError', e);
      if (e.message.search(/^(?=.*422).*$/) > -1) {
        globalState.setErrors({
          message: 'エラーが発生しました。 名前は既に存在します',
          name: 'NonFatalError',
        });
      }
      if (e.message.search(/^(?=.*403).*$/) > -1) {
        globalState.setErrors({
          message: 'エラーが発生しました。 権限がありません',
          name: 'NonFatalError',
        });
      }
    },
  });

  /* eslint no-empty-pattern: 0 */
  const [runCopyGoal, { loading: copyGoalLoading }] = useMutation(CopyConversionGoalDocument, {
    context: { clientName: 'planbcd' },
    variables: {
      id: organization_id,
      input: {
        organization_id: organization_id,
        src_goal_id: selectedGoalId,
      },
    },
    onCompleted: () => {
      refetch();
    },
    onError: (e) => {
      console.log('onError', e);
      refetch();
    },
  });

  /* eslint no-empty-pattern: 0 */
  const [runDeleteGoal, { loading: deleteGoalLoading }] = useMutation<void>(DeleteConversionGoalDocument, {
    context: { clientName: 'planbcd' },
    variables: {
      id: selectedGoalId,
    },
    onCompleted: () => {
      refetch();
    },
    onError: (e) => {
      console.log('onError', e);
      refetch();
    },
  });

  useEffect(() => {
    if (goal.length > 0) {
      setGoalFormatError(goal.length > 50);
    }
  }, [goal]);

  useEffect(() => {
    if (description.length > 0) {
      setDescriptionFormatError(description.length > 120);
    }
  }, [description]);

  const togglePostDrawer = (openPostDrawer: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setOpenPostDrawer(openPostDrawer);
  };

  const closePostDrawer = () => {
    setGoal('');
    setOpenPostDrawer(false);
  };

  const handleGoalChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.target.value.length <= 50) {
      setGoal(event.target.value);
    }
  };

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.target.value.length <= 120) {
      setDescription(event.target.value);
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRadioValue((event.target as HTMLInputElement).value);
  };

  const handleTransactionRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTransactionRadioValue((event.target as HTMLInputElement).value);
  };

  const handleSubmit = () => {
    if (organization_id && goal) runCreateGoal();
  };

  const handleModalOpen = () => {
    setOpenDelete(true);
  };

  const handleModalClose = () => {
    setOpenDelete(false);
  };

  /**
   * Table Order
   */
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderType>('id');
  const handleRequestSort = (property: OrderType) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  return (
    <div className={className}>
      <StyledBackdrop open={createGoalLoading || copyGoalLoading || deleteGoalLoading}>
        <CircularProgress color="inherit" />
      </StyledBackdrop>
      <Dialog open={openDelete} onClose={handleModalClose}>
        <DialogTitle>ゴールの削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            このゴールを削除します。ゴールを削除すると過去のレポートデータも削除されます。
          </DialogContentText>
          <DialogContentText>よろしければ次にお進みください。</DialogContentText>
          {!selectedGoalLoading && selectedGoal && selectedGoal.conversionGoal ? (
            <TableContainer component={StyledPaper}>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableHeaderCell style={{ minWidth: 128 }}>ゴール名</StyledTableHeaderCell>
                    <StyledTableCell>
                      <GoalTitleWrapper>{selectedGoal.conversionGoal.title}</GoalTitleWrapper>
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableHeaderCell style={{ minWidth: 128 }}>ゴールID</StyledTableHeaderCell>
                    <StyledTableCell>{selectedGoal.conversionGoal.id}</StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableHeaderCell style={{ minWidth: 128 }}>ゴール概要</StyledTableHeaderCell>
                    <StyledTableCell>
                      <GoalTitleWrapper>{selectedGoal.conversionGoal.description}</GoalTitleWrapper>
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableHeaderCell style={{ minWidth: 128 }}>ゴール種別</StyledTableHeaderCell>
                    <StyledTableCell>
                      {selectedGoal.conversionGoal.trigger.type === 'page_visit'
                        ? 'ページ訪問'
                        : selectedGoal.conversionGoal.trigger.type === 'click'
                        ? 'クリックトラッキング'
                        : selectedGoal.conversionGoal.trigger.type === 'transaction'
                        ? 'トランザクション'
                        : selectedGoal.conversionGoal.trigger.type === 'event'
                        ? 'イベントトラッキング'
                        : null}
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
          {!selectedGoalLoading &&
          selectedGoal &&
          selectedGoal.conversionGoal?.projects &&
          selectedGoal.conversionGoal.projects.length > 0 ? (
            <>
              <DialogContentText style={{ marginTop: 8 }}>ゴールが利用されているエクスペリエンス一覧</DialogContentText>
              {selectedGoal.conversionGoal.projects.map((project) => {
                return (
                  <StyledLink to={`/engine/experiments/${project?.id}`} key={project?.id} target="_blank">
                    <UrlLink>
                      {project?.title}{' '}
                      {project?.primary_goal_id === selectedGoal.conversionGoal.id ? ' (メインゴール)' : null}
                    </UrlLink>
                  </StyledLink>
                );
              })}
              <DialogContentText>
                {selectedGoal.conversionGoal.projects.findIndex(
                  (project) => project?.primary_goal_id === selectedGoal.conversionGoal.id,
                ) >= 0 ? (
                  <WarningText>メインゴールに設定されているため、このゴールは削除することができません。</WarningText>
                ) : null}
              </DialogContentText>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={() => {
              handleModalClose();
            }}
          >
            キャンセル
          </SecondaryButton>
          <PrimaryButton
            onClick={() => {
              runDeleteGoal();
              handleModalClose();
            }}
            loading={selectedGoalLoading}
            disabled={
              selectedGoal &&
              selectedGoal.conversionGoal?.projects &&
              selectedGoal?.conversionGoal?.projects?.findIndex(
                (project) => project?.primary_goal_id === selectedGoal.conversionGoal.id,
              ) >= 0
                ? true
                : false
            }
          >
            削除する
          </PrimaryButton>
        </DialogActions>
      </Dialog>
      <StyledDrawer
        anchor="right"
        open={openPostDrawer}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            togglePostDrawer(false);
          }
        }}
      >
        <DrawerContainer>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>ゴールを新規作成する</DrawerTitle>
            </DrawerHeader>
            <Content>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <StyledInputField
                    value={goal}
                    text="ゴールの名前"
                    onChange={handleGoalChange}
                    error={goalFormatError}
                  />
                  {goalFormatError && <InputError>50文字以内で入力してください</InputError>}
                </Grid>
                <Grid item xs={12}>
                  <StyledTextarea
                    value={description}
                    text="ゴールの概要"
                    onChange={handleDescriptionChange}
                    error={goalFormatError}
                    required={false}
                  />
                  {descriptionFormatError && <InputError>120文字以内で入力してください</InputError>}
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">ゴールのタイプ</FormLabel>
                    <RadioGroup aria-label="gender" name="gender1" value={radioValue} onChange={handleRadioChange}>
                      <FormControlLabel value="page_visit" control={<Radio />} label="ページ訪問" />
                      <FormControlLabel value="click" control={<Radio />} label="クリックトラッキング" />
                      <FormControlLabel value="transaction" control={<Radio />} label="トランザクション" />
                      <FormControlLabel value="event" control={<Radio />} label="イベントトラッキング" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {radioValue === 'transaction' ? (
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">トラッキングコードで送信する情報</FormLabel>
                      <RadioGroup
                        aria-label="gender"
                        name="gender1"
                        value={transactionRadioValue}
                        onChange={handleTransactionRadioChange}
                      >
                        <FormControlLabel value="amount" control={<Radio />} label="総額" />
                        <FormControlLabel value="quantity" control={<Radio />} label="数量" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                ) : null}
              </Grid>
            </Content>
          </DrawerContent>
          <DrawerFooter>
            <DrawerCancelButton onClick={closePostDrawer}>キャンセル</DrawerCancelButton>
            <DrawerButton onClick={handleSubmit} disabled={!goal || goalFormatError}>
              保存する
            </DrawerButton>
          </DrawerFooter>
        </DrawerContainer>
      </StyledDrawer>
      <PageContainer>
        <PageHeader>
          <PageTitle>ゴール設定</PageTitle>
          {k2organization?.getOrganization.permissions.create_event_goals.permit ? (
            <PaperButton onClick={togglePostDrawer(true)}>ゴールを新規作成する</PaperButton>
          ) : null}
        </PageHeader>
        {!loading && !organizationLoading ? (
          k2organization?.getOrganization.permissions.create_event_goals.permit ? (
            data && data.conversionGoals.length > 0 ? (
              <StyledPaper>
                <PaperContent>
                  <NoPaddingPaperItem>
                    <PaperText>エクスペリエンスで利用するゴールの新規作成、編集、コピー、削除を行います。</PaperText>
                  </NoPaddingPaperItem>
                  <PaperItem>
                    <Table>
                      <TableHead>
                        <StyledTableRow>
                          <StyledTableHeaderCell
                            sortDirection={orderBy === 'id' ? order : false}
                            style={{ minWidth: 120 }}
                          >
                            <TableSortLabel
                              active={orderBy === 'id'}
                              direction={orderBy === 'id' ? order : 'asc'}
                              onClick={() => {
                                handleRequestSort('id');
                              }}
                            >
                              ゴールID
                            </TableSortLabel>
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell sortDirection={orderBy === 'title' ? order : false}>
                            <TableSortLabel
                              active={orderBy === 'title'}
                              direction={orderBy === 'title' ? order : 'asc'}
                              onClick={() => {
                                handleRequestSort('title');
                              }}
                            >
                              ゴール名
                            </TableSortLabel>
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell
                            sortDirection={orderBy === 'trigger_type' ? order : false}
                            style={{ minWidth: 210 }}
                          >
                            <TableSortLabel
                              active={orderBy === 'trigger_type'}
                              direction={orderBy === 'trigger_type' ? order : 'asc'}
                              onClick={() => {
                                handleRequestSort('trigger_type');
                              }}
                            >
                              ゴール種別
                            </TableSortLabel>
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell
                            sortDirection={orderBy === 'projects_count' ? order : false}
                            style={{ minWidth: 110 }}
                          >
                            <TableSortLabel
                              active={orderBy === 'projects_count'}
                              direction={orderBy === 'projects_count' ? order : 'asc'}
                              onClick={() => {
                                handleRequestSort('projects_count');
                              }}
                            >
                              利用数
                            </TableSortLabel>
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell style={{ minWidth: 92 }}>オプション</StyledTableHeaderCell>
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        {/* @ts-ignore */}
                        {stableSort<ConversionGoal>(data.conversionGoals, getComparator(order, orderBy)).map((goal) => {
                          return (
                            <TableRow key={goal.id}>
                              <StyledTableCell>{goal.id}</StyledTableCell>
                              <StyledTableCell>
                                <GoalTitleWrapper>
                                  <StyledLink to={`${goal.id}`}>{goal.title}</StyledLink>
                                  {goal.description}
                                </GoalTitleWrapper>
                              </StyledTableCell>
                              <StyledTableCell>
                                <GoalTypeWrapper>
                                  <GoalTypeIconWrapper>
                                    {goal.trigger.type ? <GoalTypeIcon goalType={goal.trigger.type} /> : null}
                                  </GoalTypeIconWrapper>
                                  {goal.trigger.type ? getGoalType(goal.trigger.type, goal.transaction_type) : null}
                                </GoalTypeWrapper>
                              </StyledTableCell>
                              <StyledTableCell>{goal.projects_count}</StyledTableCell>
                              <StyledTableCell>
                                <IconButton
                                  aria-haspopup="true"
                                  onClick={(event) => {
                                    handleMenuClick(event, goal.id);
                                  }}
                                >
                                  <MoreHorizIcon />
                                </IconButton>
                              </StyledTableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </PaperItem>
                </PaperContent>
              </StyledPaper>
            ) : (
              <StyledPaper>
                <PaperTitle>
                  <PaperTitleText>初めての方へ</PaperTitleText>
                </PaperTitle>
                <PaperContent>
                  <PaperItem>
                    <PaperText>
                      {
                        'Kaizen Platform では特定のページへの遷移をエクスペリエンスのゴールに設定することができます。\nたとえば、会員登録や購入の完了画面を「メインゴール」として設定できる他、それらの遷移の手前にあるフォームページや確認画面も「サブゴール」としてゴール設定することが可能です。\nゴール設定したページへの遷移率を基準に、各エクスペリエンスの勝敗や、アクセス数の配分の割合が決定されます。\n一度ゴール設定したページは、どのエクスペリエンスでも再利用することができますので、あらかじめサイトの主要導線を登録しておくことで効率的なエクスペリエンスの運用が実現できます。'
                      }
                    </PaperText>
                    <ExperimentImage src={ExperimentGoalsImage} />
                  </PaperItem>
                </PaperContent>
                <PaperTitle>
                  <PaperTitleText>ゴールタイプの種類と選び方</PaperTitleText>
                </PaperTitle>
                <PaperContent>
                  <PaperItem>
                    <PaperText>
                      {
                        'ゴールタイプにより、コンバージョンの計測方法や設定内容が異なります。\n以下ご参考にテストの目的にあったゴールタイプをご選択ください。\nいずれのゴールタイプとも計測するには、Kaizen PlatformのJava Scriptタグの設定が完了している必要があります。'
                      }
                    </PaperText>
                  </PaperItem>
                </PaperContent>
                <PaperContent>
                  <PaperItem>
                    <Table>
                      <TableHead>
                        <StyledTableRow>
                          <StyledTableHeaderCell style={{ width: '20%' }}>ゴールタイプ</StyledTableHeaderCell>
                          <StyledTableHeaderCell style={{ width: '40%' }}>
                            計測対象のユーザーアクション
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell style={{ width: '40%' }}>設定方法</StyledTableHeaderCell>
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <StyledTableCell>
                            <TableCellWithIcon>
                              ページ訪問
                              <GoalTypeIcon goalType="page_visit" />
                            </TableCellWithIcon>
                          </StyledTableCell>
                          <StyledTableCell>
                            ユーザーがフォーム登録完了画面や決済完了画面など特定のページへアクセスしたときに1コンバージョンとして計測します。
                          </StyledTableCell>
                          <StyledTableCell>ゴール設定画面で訪問対象ページのURLを登録します。</StyledTableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell>
                            <TableCellWithIcon>
                              イベントトラッキング
                              <GoalTypeIcon goalType="event" />
                            </TableCellWithIcon>
                          </StyledTableCell>
                          <StyledTableCell>
                            ユーザーがページ内のボタンや画像、リンクテキストなどをクリックしたときに1コンバージョンとして計測します。
                          </StyledTableCell>
                          <StyledTableCell>
                            ゴール設定画面で対象ページのURLおよび個々のイベント対象UIの該当HTMLタグを登録します。
                            クリックトラッキングと異なり、対象ページのHTMLファイル内にゴール計測用のIDを設置する必要がありません。
                          </StyledTableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell>
                            <TableCellWithIcon>
                              トランザクション
                              <GoalTypeIcon goalType="transaction" />
                            </TableCellWithIcon>
                          </StyledTableCell>
                          <StyledTableCell>
                            ECサイトの購入金額などゴール到達の価値が一定ではない場合に使用します。
                            ユーザーが購入完了画面など特定ページへアクセスしたときに「総額」または「数量」をコンバージョンとして計上します。
                          </StyledTableCell>
                          <StyledTableCell>
                            ゴール設定画面でページ訪問対象ページのURLおよび「1訪問あたりの総額」あるいは「1訪問あたりの数量」を設定します。
                          </StyledTableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell>
                            <TableCellWithIcon>
                              クリックトラッキング
                              <GoalTypeIcon goalType="click" />
                            </TableCellWithIcon>
                          </StyledTableCell>
                          <StyledTableCell>
                            イベントトラッキングと同様、ユーザーがページ内のボタンや画像、リンクテキストなどをクリックしたときに1コンバージョンとして計測します。
                          </StyledTableCell>
                          <StyledTableCell>
                            イベントトラッキングのご利用をお勧めします。ゴール設定画面での設定のほか、予め、対象ページのHTMLファイル内のイベント対象UIのHTMLタグにゴール設定画面内から取得できるゴール計測用のIDを設置する必要があります。
                          </StyledTableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </PaperItem>
                  <PaperItemButtonWrapper>
                    <PaperButton onClick={togglePostDrawer(true)}>ゴールを新規作成する</PaperButton>
                  </PaperItemButtonWrapper>
                </PaperContent>
              </StyledPaper>
            )
          ) : (
            <StyledPaper>
              <PaperTitle>
                <PaperTitleText>指定されたページにアクセスできません</PaperTitleText>
              </PaperTitle>
            </StyledPaper>
          )
        ) : (
          <StyledLoading />
        )}
      </PageContainer>
      <Menu id="simple-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            runCopyGoal();
            handleMenuClose();
          }}
        >
          コピーする
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(`${selectedGoalId}`);
          }}
        >
          編集する
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleModalOpen();
            handleMenuClose();
          }}
        >
          削除する
        </MenuItem>
      </Menu>
    </div>
  );
};

const Content = styled('div')(`
  height: 100%;
  width: 100%;
`);

const StyledTableRow = styled(TableRow)(`
  background: #FFFFFF;
`);

const StyledTableHeaderCell = styled(TableCell)(`
  font-weight: 700;
  font-size: 12px;
  color: ${COLOR_TEXT_MAIN};
`);

const StyledTableCell = styled(TableCell)(`
  font-size: 12px;
  color: ${COLOR_TEXT_MAIN};
`);

const StyledLink = styled(Link)(`
  color: #006BD6;
  font-size: 12px;
  font-weight: 400;
  text-decoration: none;
`);

const InputError = styled(Typography)(`
  color: #FE7877;
`);

const StyledLoading = styled(Loading)(`
  margin-top: 24px;
  height: 240px;
`);

const GoalTypeWrapper = styled('div')(`
  display: flex;
  align-items: center;
`);

const GoalTypeIconWrapper = styled('div')(`
  margin-right: 8px;
  display: flex;
  align-items: center;
`);

const NoPaddingPaperItem = styled(PaperItem)`
  padding: 0px;
`;

const ExperimentImage = styled('img')`
  margin: 24px 0px;
  width: 100%;
`;

const TableCellWithIcon = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PaperItemButtonWrapper = styled(PaperItem)`
  display: flex;
  justify-content: flex-end;
`;

const GoalTitleWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  overflow-wrap: anywhere;
`;

const PrimaryButton = styled(LoadingButton)(`
  color: ${COLOR_BUTTON_TEXT};
  height: 40px;
  font-size: 14px;
  background: ${COLOR_TEXT_MAIN};
  font-weight: 700;
  border-color: ${COLOR_TEXT_MAIN};
  border-radius: 4px;
  padding: 12px 24px;
  text-transform: none;
  :hover {
    background: ${COLOR_EFFECT};
    border-color: ${COLOR_EFFECT};
  }
  :disabled {
    background: ${COLOR_DISABLE_BACKGROUND};
    color: ${COLOR_TEXT_DISABLE};
    border: ${COLOR_TEXT_DISABLE};
  }
`);

const SecondaryButton = styled(Button)<{ width?: string }>(`
  color: ${COLOR_TEXT_MAIN};
  height: 40px;
  padding: 13px 16px;
  font-size: 14px;
  background: ${COLOR_BUTTON_BACKGROUND};
  font-weight: 700;
  border: 1.5px solid ${COLOR_BORDER};
  border-radius: 4px;
  margin-right: 16px;
  :hover {
    background: ${COLOR_BUTTON_BACKGROUND};
    color: ${COLOR_EFFECT};
  }
`);

const UrlLink = styled(Typography)`
  color: #006bd6;
  font-weight: 400;
  font-size: 14px;
`;

const WarningText = styled(Typography)`
  color: #f5b800;
  margin-top: 8px;
  font-weight: 400;
`;

export default GoalSettingPage;
